import { GoFormzServices } from '../AppSettings';
import { analyticsIdentify, analyticsGroup, LoadAnalytics } from '../helpers/AnalyticsUtility';
import { apimCall } from '../helpers/api';
import { initInactivityTimeout } from '../helpers/InactivityTimeoutHelper';
import { Features } from '../helpers/PermissionUtility';
import { raygunIdentify } from '../helpers/RaygunUtility';
import { disableRecording } from '../helpers/ScreenRecordingUtility';
import { SetSiftUserId } from '../Sift';
import { setThemeInDom, ThemeValue } from '../theme';
import { ProfileState } from './profile-types';

export const GET_PROFILE_REQUEST = 'GET_PROFILE_REQUEST';
export const GET_PROFILE_SUCCESS = 'GET_PROFILE_SUCCESS';
export const GET_PROFILE_FAILURE = 'GET_PROFILE_FAILURE';
export const UPDATE_PROFILE_SIGNATURE = 'UPDATE_PROFILE_SIGNATURE';

export const getProfile = () => async (dispatch) => {
  dispatch({ type: GET_PROFILE_REQUEST });

  try {
    const profileResult = await apimCall(GoFormzServices.AccountService, '/profile');
    dispatch({
      type: GET_PROFILE_SUCCESS,
      payload: profileResult.data
    });

    const profile = profileResult.data;
    const userInfo = profile.user || profile.independentUser;

    setTheme(profile);

    // dont allow recording of hipaa clients
    if (profile?.client?.isHIPAA) {
      disableRecording();
    }

    if (profile.user != null) {
      LoadAnalytics();
      identifyAnalytics(profile);
    }

    if (profile.independentUser != null && profile.user == null) {
      LoadAnalytics(true);
      identifyIuAnalytics(profile);
    }

    SetSiftUserId(userInfo.email);

    initInactivityTimeout();
  } catch (err) {
    console.error(err);
    dispatch({ type: GET_PROFILE_FAILURE });
  }
};

export const reloadProfile = () => async (dispatch) => {
  try {
    const profileResult = await apimCall(GoFormzServices.AccountService, '/profile');
    dispatch({
      type: GET_PROFILE_SUCCESS,
      payload: profileResult.data
    });

    identifyAnalytics(profileResult.data);
    setTheme(profileResult.data);
  } catch (err) {
    console.error(err);
    dispatch({ type: GET_PROFILE_FAILURE });
  }
};

export const updateProfileSignature = (signature, fieldType) => (dispatch) => {
  dispatch({
    type: UPDATE_PROFILE_SIGNATURE,
    payload: signature,
    fieldType
  });
};

export const getPublicShareProfile = () => (dispatch) => {
  dispatch({
    type: GET_PROFILE_SUCCESS,
    payload: {
      user: {},
      client: {},
      groupIds: []
    }
  });
};

function setTheme(profile: ProfileState) {
  const features = profile.features ?? {};
  const baseTheme = !!features[Features.DarkMode] ? 'dark' : 'light';
  const isAutomatedTesting = !!features[Features.AutomatedTesting] ? '-automation' : '';
  const theme = `${baseTheme}${isAutomatedTesting}` as ThemeValue;
  setThemeInDom(theme);
}

function identifyAnalytics(profile: ProfileState) {
  const client = profile.client;
  const userInfo = profile.user;

  //other traits are added in the backend
  const clientData = {
    clientId: client.id,
    usagedataid: client.id,
    groupId: client.id //some segment destinations only allow groupId
  };

  const userData = {
    usagedataid: client.id, //legacy clientId
    clientId: client.id,
    groupId: client.id, //some segment destinations only allow groupId

    //this data has to be sent here in addition to the backend in order for intercom to recieve it.
    //only add data exclusive to intercom
    name: userInfo.fullName,
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    email: userInfo.email,
    username: userInfo.email,
    phone: userInfo.phoneNumber,
    createdAt: userInfo.createdDate,
    isAdmin: profile.isAdmin,
    inSalesforce: userInfo.inSalesforce,
    clientName: client.name,
    clientLicenses: client.licenseCount,
    clientSubscription: client.subscription,
    clientPromoCode: client.promoCode,
    clientCreatedAt: client.createdDate,
    clientTrialStartedAt: client.createdDate,
    clientTrialEndsAt: client.trialEndDate,
    clientSubscriptionStartedAt: client.subscriptionStartDate,
    clientSubscriptionEndedAt: client.subscriptionEndDate,
    clientBillingPeriod: client.billingPeriod,
    clientMaintenanceMode: client.maintenanceMode,
    clientBucket: client.bucket,
    clientIndustry: client.industry,
    clientCompanySize: client.companySize
  };

  analyticsIdentify(userInfo.id, userData);
  analyticsGroup(client.id, clientData);
  raygunIdentify(userInfo.id, userData);
}

function identifyIuAnalytics(profile: ProfileState) {
  const independentUserInfo = profile.independentUser;

  const userData = {
    name: independentUserInfo.fullName,
    firstName: independentUserInfo.firstName,
    lastName: independentUserInfo.lastName,
    email: independentUserInfo.email,
    username: independentUserInfo.email,
    phone: independentUserInfo.phoneNumber
  };

  analyticsIdentify(independentUserInfo.independentUserId, userData);
  analyticsGroup(independentUserInfo.independentUserId, userData);
  raygunIdentify(independentUserInfo.independentUserId, userData);
}
